import {Component, inject, input, OnChanges, output, SimpleChanges, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors
} from "@angular/forms";
import {CTMSMilestone} from "../../models/ctmsmilestone.model";
import {StrategicPillar, StrategicPillarService} from "../../services/strategic-pillar.service";
import {
  RdsBadgeModule,
  RdsButtonModule,
  RdsDividerModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsProgressSpinnerComponent,
  RdsRadioButtonModule,
  RdsSwitchModule,
} from "@rds/angular-components";
import {FeedbackService} from "../../services/feedback.service";
import {FormValidity} from "../detail-view-wrapper.component";
import {ValidationErrorCollector} from "../../models/validation-error-collector.model";
import {DateFormatter} from "../../models/date-formatter.model";
import {ExpandableInputComponent} from "../expandable-input/expandable-input.component";
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { firstValueFrom } from 'rxjs';
import { AudienceService, UserAudienceResponse, UnifiedAudience } from '../../services/audience.service';
import { PillarsAudiencesSectionComponent } from '../../shared/pillars-audiences-section/pillars-audiences-section.component';

export type VisibilityType = 'visible' | 'hidden'

type DetailedViewForm = FormGroup<{
  metadata: FormGroup<{
    projectName: FormControl<string>;
    studyDescription: FormControl<string | null>;
    activityName: FormControl<string>;
    date: FormControl<string>;
  }>;
  keyWords: FormArray<FormControl<string | null>>;
  link: FormControl<string | null>;
  notes: FormControl<string | null>;
  isVisible: FormControl<boolean | null>;
}>

@Component({
  selector: 'app-ctmsmilestone-detail-view',
  standalone: true,
  imports: [
    RdsButtonModule,
    RdsDividerModule,
    RdsFormFieldModule,
    RdsIconComponent,
    RdsRadioButtonModule,
    RdsBadgeModule,
    ReactiveFormsModule,
    RdsProgressSpinnerComponent,
    RdsSwitchModule,
    ExpandableInputComponent,
  PillarsAudiencesSectionComponent,
  ],
  templateUrl: './ctmsmilestone-detail-view.component.html',
  styleUrls: ['./ctmsmilestone-detail-view.component.scss']
})
export class CtmsmilestoneDetailViewComponent implements OnChanges, OnInit {
  milestone = input.required<CTMSMilestone>()
  isLoadingChanges = input.required<boolean>()
    newCTMSMilestone = output<{ data: CTMSMilestone, valid: FormValidity, strategicPillarIds: number[], audiences: UnifiedAudience[] }>()
  formBuilder = inject(FormBuilder)
  form!: DetailedViewForm
  editorActionsVisible: VisibilityType = 'hidden'
  isViewer: boolean = true
  @ViewChild(PillarsAudiencesSectionComponent) pillarsAudiencesSection!: PillarsAudiencesSectionComponent;

  // Unified section encapsulates strategic pillar selector
  private strategicPillarService = inject(StrategicPillarService);
    pendingStrategicPillars: { pillarIds: number[], pillars: StrategicPillar[] } | null = null;
    pendingAudiences: { audienceIds: number[], audiences: UnifiedAudience[] } | null = null;
  
  constructor(private feedbackService: FeedbackService, private userService: UserService, private audienceService: AudienceService){}

  ngOnInit() {
    this.userService.getUserData().then((user: User) => {
      this.editorActionsVisible = user.isViewer ? 'hidden' : 'visible'
      this.isViewer = user.isViewer
      this.setupForm()
    }).catch((error: any) => {
      console.error('Error fetching user data:', error);
    });
  }

  onAudienceSelectionChange(selection: { audienceIds: number[], audiences: UnifiedAudience[] }) {
    // Store the pending change for when user clicks Save
    this.pendingAudiences = selection;
    console.log('Audience selection changed:', selection);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['milestone']){
      this.setupForm()
    }
  }

  private setupForm(){
    const m = this.milestone()
    const presentationDate = new DateFormatter(m.metadata.date, false).presentationDate

    this.form = this.formBuilder.group({
      metadata: this.formBuilder.group({
        projectName: this.formBuilder.control({value: m.metadata.projectName, disabled: true}, {nonNullable: true}),
        studyDescription: this.formBuilder.control({value: m.metadata.studyDescription, disabled: true}),
        activityName: this.formBuilder.control({value: m.metadata.activityName, disabled: true}, {nonNullable: true}),
        date: this.formBuilder.control({value: presentationDate, disabled: true}, {nonNullable: true}),
      }),
      keyWords: this.formBuilder.array(m.keyWords.map(k => this.formBuilder.control({value: k, disabled: this.isViewer}, this.keywordValidator))),
      link: this.formBuilder.control({value: m.link, disabled: this.isViewer}),
      notes: this.formBuilder.control({value: m.notes, disabled: this.isViewer}),
      isVisible: this.formBuilder.control({
        value: m.isVisible == false ? m.isVisible : true, 
        disabled: this.isViewer})
    })
  }

  keywordValidator(keywordControl: AbstractControl<string>) {
    const keyword = keywordControl.value
    if (!keyword || typeof keyword !== 'string' || keyword.trim() == "")
      return {emptyKeywordError: true} as ValidationErrors
    else
      return null
  }

  addKeyword() {
    this.form.controls.keyWords.push(this.formBuilder.control("", this.keywordValidator))
  }

  removeKeyword(i: number){
    this.form.controls.keyWords.removeAt(i)
  }

  clearEmptyKeywords(){
    const indices: [string | null, number][] = this.form.controls.keyWords.value.map((x, i) => [x, i] as [string | null, number]).filter(k => k[0] === "")
    for(let [offset, idx] of indices.entries()){
      this.form.controls.keyWords.removeAt(idx[1] - offset)
    }
  }

  onKeywordKeypress(e: KeyboardEvent){
    if(e.code == "Enter"){
      e.preventDefault()
    }
  }

  onSubmit() {
    // Close any open dropdowns before submitting
    this.pillarsAudiencesSection?.closeAllDropdowns();
    
    const errors = new ValidationErrorCollector(this.form).errors;
    if (errors["emptyKeywordError"]) {
      this.clearEmptyKeywords();
    }

    // Always allow saving to test strategic pillar functionality
    const validity: FormValidity = this.form.valid ? 'valid' : 'warning';
    const copy = Object.assign(new CTMSMilestone(), JSON.parse(JSON.stringify(this.milestone())));
    const post = Object.assign(copy, this.form.value) as CTMSMilestone;

    // Mark form as pristine to avoid duplicate submissions
    this.form.markAsPristine();

    const existingPillarIDs = (this.milestone().strategicPillars || []).map(p => p.id);
    const existingAudiences = (this.milestone() as any).audiences || [];

    this.newCTMSMilestone.emit({ 
      data: post, 
      valid: validity,
      strategicPillarIds: this.pendingStrategicPillars ? this.pendingStrategicPillars.pillarIds : existingPillarIDs,
      audiences: this.pendingAudiences ? this.pendingAudiences.audiences : existingAudiences
    });
  }

  openLink(url: string = this.form.controls.link.value || '') { 
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    const newWindow = window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  onPillarChanged(selection: { pillarIds: number[], pillars: StrategicPillar[] }) {
    // Store the pending change for when user clicks Save
    this.pendingStrategicPillars = selection;
    console.log('Strategic pillar selection changed:', selection);
  }
}
